import { useState, useEffect, useRef } from "react";

interface FormValues {
  name: string;
  account_id: string;
  currency: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: FormValues) => Promise<void>;
}

const CURRENCIES = ["USD", "EUR", "GBP", "CHF", "CAD", "AUD", "JPY", "HKD", "SGD"];

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 12,
  padding: "28px 32px",
  width: 420,
  maxWidth: "calc(100vw - 32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  marginBottom: 5,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 7,
  fontSize: 14,
  color: "#111827",
  outline: "none",
  background: "#f9fafb",
};

const rowStyle: React.CSSProperties = {
  marginBottom: 18,
};

export default function NewPortfolioModal({ open, onClose, onSubmit }: Props) {
  const [values, setValues] = useState<FormValues>({ name: "", account_id: "", currency: "USD" });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValues({ name: "", account_id: "", currency: "USD" });
      setError(null);
      setLoading(false);
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const set = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setValues((v) => ({ ...v, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) { setError("Portfolio name is required."); return; }
    if (!values.account_id.trim()) { setError("Account ID is required."); return; }
    setError(null);
    setLoading(true);
    try {
      await onSubmit({ name: values.name.trim(), account_id: values.account_id.trim(), currency: values.currency });
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ??
        (err as { message?: string })?.message ??
        "Failed to create portfolio.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <h2 id="modal-title" style={{ margin: "0 0 22px", fontSize: 18, color: "#1e293b", fontWeight: 700 }}>
          New Portfolio
        </h2>

        <form onSubmit={handleSubmit} noValidate>
          {/* Portfolio Name */}
          <div style={rowStyle}>
            <label htmlFor="pf-name" style={labelStyle}>Portfolio Name</label>
            <input
              ref={nameRef}
              id="pf-name"
              type="text"
              placeholder="e.g. Main Account"
              value={values.name}
              onChange={set("name")}
              style={inputStyle}
              autoComplete="off"
            />
          </div>

          {/* Account ID */}
          <div style={rowStyle}>
            <label htmlFor="pf-account" style={labelStyle}>IBKR Account ID</label>
            <input
              id="pf-account"
              type="text"
              placeholder="e.g. U1234567"
              value={values.account_id}
              onChange={set("account_id")}
              style={inputStyle}
              autoComplete="off"
            />
          </div>

          {/* Currency */}
          <div style={rowStyle}>
            <label htmlFor="pf-currency" style={labelStyle}>Base Currency</label>
            <select
              id="pf-currency"
              value={values.currency}
              onChange={set("currency")}
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {error && (
            <div style={{ marginBottom: 14, fontSize: 13, color: "#ef4444", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "8px 12px" }}>
              {error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: "9px 20px",
                borderRadius: 7,
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#374151",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "9px 22px",
                borderRadius: 7,
                border: "none",
                background: loading ? "#93c5fd" : "#3b82f6",
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Creating…" : "Create Portfolio"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
